import { motion } from "framer-motion";
import { Home, KeyRound, MousePointerClick, DoorOpen, UsersRound, HeartHandshake } from "lucide-react";

const cases = [
  {
    icon: Home,
    title: "Seller Leads",
    tag: "Listing appointments",
    desc: "Home valuation requests and listing inquiries get a fast response and a clear path to a listing appointment — even after hours.",
  },
  {
    icon: KeyRound,
    title: "Buyer Leads",
    tag: "Showings & consultations",
    desc: "New buyer inquiries are qualified on timeline and financing readiness, then guided toward a showing or buyer consultation.",
  },
  {
    icon: MousePointerClick,
    title: "Portal Inquiries",
    tag: "Zillow & portal follow-up",
    desc: "Zillow and other portal leads get consistent, immediate follow-up before they move on to the next agent.",
  },
  {
    icon: DoorOpen,
    title: "Open-House Follow-Up",
    tag: "Weekend sign-ins",
    desc: "Sign-in sheets become structured follow-up conversations — not names forgotten in a spreadsheet.",
  },
  {
    icon: UsersRound,
    title: "Team & Broker Lead Routing",
    tag: "Round-robin & routing",
    desc: "Incoming leads are captured, qualified, and routed to the right agent — with visibility into what happened next.",
  },
  {
    icon: HeartHandshake,
    title: "Sphere & Past Clients",
    tag: "Database nurture",
    badge: "Long-Term",
    desc: "Stay in front of your database with light-touch nurture that surfaces referrals and repeat business when the timing is right.",
  },
];

export default function UseCases() {
  return (
    <section id="use-cases" data-testid="use-cases-section" className="py-20 md:py-28 bg-[#0B0F19]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            Built for How <span className="text-gradient">Real Estate Actually Works</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-slate-200 leading-relaxed">
            Every engagement starts with one goal: turning new buyer and seller inquiries into
            appointments, clients, and closings.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`use-case-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-[border-color] duration-300 hover:border-cyan-400/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-cyan-500/10 text-cyan-300 flex items-center justify-center shrink-0">
                  <c.icon size={20} strokeWidth={1.75} />
                </div>
                {c.badge && (
                  <span className="rounded-full bg-indigo-500/15 text-indigo-300 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                    {c.badge}
                  </span>
                )}
              </div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-400/80 mb-1.5">{c.tag}</p>
              <h3 className="font-heading font-bold text-xl text-white mb-2.5">{c.title}</h3>
              <p className="text-slate-300 text-base leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
